import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@toki/auth';
import { CaretLeft, CaretRight, DownloadSimple, FilePdf } from '@phosphor-icons/react';
import type {
  InformeColumna,
  InformeFila,
  InformeFiltros,
  InformeKpi,
  InformeResultado,
  InformeTabla,
  InformeTipo,
  InformesCatalogo,
} from '../services/api';
import { avisarInformeImpreso, downloadInforme, getInforme, getInformesCatalogo } from '../services/api';
import { Topbar } from '../components/Topbar';
import { getModuleColor } from '../config/modules';
import { getModuleIcon } from '../icons';
import styles from './Informes.module.css';

/**
 * INFORMES (paso 88; maquetada según el diseño v7,
 * `docs/diseno/kivai-tickets-v7-informes.html`): la lista de informes a la
 * izquierda, agrupada, y el informe elegido a la derecha con su periodo,
 * filtros, comparación con el periodo anterior, cifras, gráfica y tablas.
 *
 * Esta pantalla no calcula NADA: pide el informe al servidor y pinta lo que
 * llega. Las definiciones viven en `backend/api/app/informes/base.py`, y el PDF
 * lo genera el servidor con el MISMO contenido. Un informe nuevo del catálogo
 * no necesita código aquí.
 *
 * Del diseño NO están (no hay datos detrás, K29): SLA, satisfacción, resueltas
 * por el asistente, «Programar envío» y «Envíos programados».
 *
 * ⚠️ ABRIR un informe queda en la auditoría, y también abrir el diálogo de
 * imprimir (`beforeprint`), por si alguien imprime con Ctrl+P en vez de
 * descargar el PDF.
 */

const mc = getModuleColor('informes');
const ModuleIcon = getModuleIcon('informes');

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto',
  'septiembre', 'octubre', 'noviembre', 'diciembre'];
const MESES_CORTOS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

/** Las barras de proporción de la tabla: los tonos del diseño (decorativos:
 *  la cifra va escrita al lado, el color no dice nada que no diga el texto). */
const PALETA_BARRAS = ['#EF4CA3', '#8E7CF5', '#0AA3B9', '#4B4AC2', '#C2267A'];

type Periodo = 'mes' | '30' | '90' | '365' | 'rango';
const PERIODOS: [Periodo, string][] = [['mes', 'Mes'], ['30', '30 días'], ['90', 'Trimestre'], ['365', '12 meses'], ['rango', 'Otro']];

/** «Hoy» en la zona de la instalación, como YYYY-MM-DD. */
function hoyEn(zona: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: zona }).format(new Date());
}

function sumarDias(iso: string, dias: number): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + dias)).toISOString().slice(0, 10);
}

function mesDe(ym: string): { desde: string; hasta: string } {
  const [y, m] = ym.split('-').map(Number) as [number, number];
  const ultimo = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const mm = String(m).padStart(2, '0');
  return { desde: `${y}-${mm}-01`, hasta: `${y}-${mm}-${String(ultimo).padStart(2, '0')}` };
}

function moverMes(ym: string, delta: number): string {
  const [y, m] = ym.split('-').map(Number) as [number, number];
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

function rotuloMes(ym: string): string {
  const [y, m] = ym.split('-').map(Number) as [number, number];
  const nombre = MESES[m - 1] ?? '';
  return `${nombre.charAt(0).toUpperCase()}${nombre.slice(1)} ${y}`;
}

/** «7 sep – 6 oct 2026», como el diseño. */
function rotuloRango(desde: string, hasta: string): string {
  const [y1, m1, d1] = desde.split('-').map(Number) as [number, number, number];
  const [y2, m2, d2] = hasta.split('-').map(Number) as [number, number, number];
  const a = `${d1} ${MESES_CORTOS[m1 - 1]}${y1 !== y2 ? ` ${y1}` : ''}`;
  return `${a} – ${d2} ${MESES_CORTOS[m2 - 1]} ${y2}`;
}

function numero(v: number): string {
  return v.toLocaleString('es-ES', { maximumFractionDigits: 1 });
}

/** Minutos de trabajo: «5 h 30 min», «45 min». */
function horas(min: number): string {
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  if (!h) return `${m} min`;
  return m ? `${h} h ${String(m).padStart(2, '0')} min` : `${h} h`;
}

/** Un tiempo transcurrido: «35 min», «26 h», «6 d 7 h». */
function duracion(min: number): string {
  min = Math.round(min);
  if (min < 60) return `${min} min`;
  const h = Math.round(min / 60);
  if (h < 48) {
    const m = min % 60;
    return min < 600 && m ? `${Math.floor(min / 60)} h ${String(m).padStart(2, '0')} min` : `${h} h`;
  }
  const d = Math.floor(h / 24);
  const rh = h % 24;
  return rh ? `${d} d ${rh} h` : `${d} d`;
}

function formatear(v: InformeFila[string] | undefined, tipo: InformeTipo, zona: string): string {
  if (v === null || v === undefined) return '—';
  if (v === '') return '';
  switch (tipo) {
    case 'entero':
      return numero(Number(v));
    case 'horas':
      return horas(Number(v));
    case 'duracion':
      return duracion(Number(v));
    case 'porcentaje':
      return `${numero(Number(v))} %`;
    case 'fecha': {
      const [y, m, d] = String(v).split('-');
      return `${d}/${m}/${y}`;
    }
    case 'fechahora':
      // Sin la coma de `es-ES` («28/05/2026, 10:00»): igual que en el Excel.
      return new Intl.DateTimeFormat('es-ES', {
        timeZone: zona, day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
      }).format(new Date(String(v))).replace(',', '');
    case 'sino':
      return v ? 'Sí' : 'No';
    default:
      return String(v);
  }
}

/** El ▲/▼ frente al periodo anterior, y si es bueno o malo. Misma regla que
 *  el PDF (`informes/pdf.py::delta`). */
function delta(k: InformeKpi): { texto: string; tono: 'bien' | 'mal' | 'neutro' } | null {
  if (k.anterior === null || k.valor === null) return null;
  let d: number;
  let txt: string;
  if (k.tipo === 'porcentaje') {
    d = Math.round((k.valor - k.anterior) * 10) / 10;
    txt = `${numero(Math.abs(d))} pts`;
  } else {
    if (!k.anterior) return { texto: k.valor ? 'Nuevo vs periodo anterior' : 'Igual que el periodo anterior', tono: 'neutro' };
    d = Math.round((1000 * (k.valor - k.anterior)) / k.anterior) / 10;
    txt = `${numero(Math.abs(d))} %`;
  }
  if (d === 0) return { texto: 'Igual que el periodo anterior', tono: 'neutro' };
  const tono = !k.mejor ? 'neutro' : (d > 0) === (k.mejor === 'sube') ? 'bien' : 'mal';
  return { texto: `${d > 0 ? '▲' : '▼'} ${txt} vs periodo anterior`, tono };
}

/** «+45 min», «−1 h 10 min», «sin cambio». */
function conSigno(min: number): string {
  if (!min) return 'sin cambio';
  return `${min > 0 ? '+' : '−'}${horas(Math.abs(min))}`;
}

const NUMERICOS = new Set<InformeTipo>(['entero', 'horas', 'duracion', 'porcentaje']);

function claseCelda(c: InformeColumna, i: number): string | undefined {
  if (NUMERICOS.has(c.tipo)) return styles.num;
  if (i === 0 || c.tipo === 'fecha' || c.tipo === 'fechahora') return styles.nowrap;
  return c.tipo === 'texto' ? styles.wide : undefined;
}

function Tabla({ tabla, zona }: { tabla: InformeTabla; zona: string }) {
  const tope = tabla.barra ? Math.max(0, ...tabla.filas.map((f) => Number(f[tabla.barra!] ?? 0))) : 0;
  const celda = (c: InformeColumna, f: InformeFila, ci: number, ri: number | null) => (
    <td key={c.clave} className={claseCelda(c, ci)}>
      {formatear(f[c.clave], c.tipo, zona)}
      {ci === 0 && ri !== null && tabla.barra && tope > 0 && (
        <span className={styles.rowBar} aria-hidden="true">
          <span style={{
            width: `${Math.round((100 * Number(f[tabla.barra] ?? 0)) / tope)}%`,
            background: PALETA_BARRAS[ri % PALETA_BARRAS.length],
          }} />
        </span>
      )}
    </td>
  );
  return (
    <div className={styles.tableScroll}>
      <table className={styles.table}>
        <thead>
          <tr>
            {tabla.columnas.map((c) => (
              <th key={c.clave} className={NUMERICOS.has(c.tipo) ? styles.num : undefined}>{c.rotulo}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tabla.filas.length === 0 && (
            <tr><td colSpan={tabla.columnas.length} className={styles.empty}>Nada en este periodo.</td></tr>
          )}
          {tabla.filas.map((f, ri) => <tr key={ri}>{tabla.columnas.map((c, ci) => celda(c, f, ci, ri))}</tr>)}
        </tbody>
        {tabla.total && tabla.filas.length > 0 && (
          <tfoot>
            <tr>{tabla.columnas.map((c, ci) => celda(c, tabla.total!, ci, null))}</tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

function Grafica({ res }: { res: InformeResultado }) {
  const g = res.grafico;
  const vals = g.tramos.flatMap((t) => [t.valor ?? 0, res.comparar ? t.anterior ?? 0 : 0]);
  const tope = Math.max(0, ...vals) * 1.08 || 1;
  const fmt = (v: number) => formatear(g.tipo === 'entero' ? Math.round(v) : v, g.tipo, res.zona);
  // El eje en corto («2,9 h»): «2 h 53 min» no cabe a 400px. Misma regla en el PDF.
  const eje = (v: number) => (g.tipo === 'horas' || g.tipo === 'duracion') && v >= 60
    ? `${numero(Math.round((v / 60) * 10) / 10)} h` : fmt(v);
  return (
    <section className={styles.card}>
      <div className={styles.chartHead}>
        <h3 className={styles.cardTitle}>{g.titulo}</h3>
        <div className={styles.chartLegend}>
          <span><i className={styles.dotCur} />Este periodo</span>
          {res.comparar && <span><i className={styles.dotPrev} />Periodo anterior</span>}
        </div>
      </div>
      <div className={styles.chart}>
        <div className={styles.chartAxis}>
          {[tope, tope / 2, 0].map((v, i) => <span key={i}>{eje(v)}</span>)}
        </div>
        <div className={styles.chartBars}>
          {g.tramos.map((t, i) => (
            <div key={i} className={styles.chartCol}
              title={`${t.rotulo}: ${t.valor === null ? '—' : fmt(t.valor)}${res.comparar ? ` (antes ${t.anterior === null ? '—' : fmt(t.anterior)})` : ''}`}>
              <div className={styles.chartPair}>
                {res.comparar && (
                  <span className={styles.barPrev} style={{ height: `${((t.anterior ?? 0) / tope) * 100}%` }} />
                )}
                <span className={styles.barCur} style={{ height: `${((t.valor ?? 0) / tope) * 100}%` }} />
              </div>
              <span className={styles.chartLabel}>{t.rotulo}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function InformesScreen() {
  const { provider } = useAuth();
  const [catalogo, setCatalogo] = useState<InformesCatalogo | null>(null);
  const [sel, setSel] = useState('volumen');
  const [error, setError] = useState<string | null>(null);

  const [periodo, setPeriodo] = useState<Periodo>('mes');
  const [hoy, setHoy] = useState<string | null>(null);
  const [mes, setMes] = useState<string | null>(null);
  const [rango, setRango] = useState<{ desde: string; hasta: string }>({ desde: '', hasta: '' });
  const [cliente, setCliente] = useState('');
  const [tecnico, setTecnico] = useState('');
  const [equipo, setEquipo] = useState('');
  const [canal, setCanal] = useState('');
  const [version, setVersion] = useState<'interna' | 'cliente'>('interna');
  const [comparar, setComparar] = useState(true);

  const [res, setRes] = useState<InformeResultado | null>(null);
  const [cargando, setCargando] = useState(false);
  const [descargando, setDescargando] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const token = await provider.getAccessToken();
      if (!token) return;
      try {
        const c = await getInformesCatalogo(token);
        setCatalogo(c);
        const h = hoyEn(c.zona);
        setHoy(h);
        setMes(h.slice(0, 7));
        setRango(mesDe(h.slice(0, 7)));
      } catch (e: any) {
        setError(e.message);
      }
    })();
  }, [provider]);

  const informe = catalogo?.informes.find((i) => i.id === sel) ?? null;
  const paraCliente = version === 'cliente' && !!informe?.versionCliente;

  const filtros: InformeFiltros | null = useMemo(() => {
    if (!mes || !hoy) return null;
    let p: { desde: string; hasta: string };
    if (periodo === 'mes') p = mesDe(mes);
    else if (periodo === 'rango') p = rango;
    else p = { desde: sumarDias(hoy, -(Number(periodo) - 1)), hasta: hoy };
    if (!p.desde || !p.hasta) return null;
    const admite = (f: string) => informe?.filtros.includes(f);
    return {
      ...p,
      cliente: admite('cliente') ? cliente || undefined : undefined,
      // K44: la versión para el cliente no se filtra por técnico ni por equipo.
      tecnico: admite('tecnico') && !paraCliente ? tecnico || undefined : undefined,
      equipo: admite('equipo') && !paraCliente ? equipo || undefined : undefined,
      canal: admite('canal') ? canal || undefined : undefined,
      version: paraCliente ? 'cliente' : 'interna',
      comparar,
    };
  }, [mes, hoy, periodo, rango, cliente, tecnico, equipo, canal, paraCliente, comparar, informe]);

  // La versión para el cliente es de UN cliente: sin cliente elegido, vuelve a interna.
  useEffect(() => {
    if (version === 'cliente' && (!cliente || cliente === 'sin')) setVersion('interna');
  }, [cliente, version]);

  const cargar = useCallback(async () => {
    if (!filtros) return;
    const token = await provider.getAccessToken();
    if (!token) return;
    setCargando(true);
    setError(null);
    try {
      setRes(await getInforme(sel, filtros, token));
    } catch (e: any) {
      setRes(null);
      setError(e.message);
    } finally {
      setCargando(false);
    }
  }, [sel, filtros, provider]);

  useEffect(() => { cargar(); }, [cargar]);

  // Ctrl+P no pasa por el servidor: al abrir el diálogo se avisa (solo audita).
  useEffect(() => {
    if (!filtros) return;
    const onPrint = async () => {
      const token = await provider.getAccessToken();
      if (token) avisarInformeImpreso(sel, filtros, token).catch(() => {});
    };
    window.addEventListener('beforeprint', onPrint);
    return () => window.removeEventListener('beforeprint', onPrint);
  }, [sel, filtros, provider]);

  const descargar = async (format: 'xlsx' | 'csv' | 'pdf', hoja: 'resumen' | 'detalle' = 'detalle') => {
    if (!filtros) return;
    const token = await provider.getAccessToken();
    if (!token) return;
    setDescargando(format);
    setError(null);
    try {
      await downloadInforme(sel, filtros, format, hoja, token);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setDescargando(null);
    }
  };

  const topbar = (
    <Topbar
      title="Informes"
      subtitle="Analiza un periodo, compáralo y compártelo"
      moduleIcon={<ModuleIcon weight="duotone" size={18} color={mc.accent} />}
      moduleColor={mc.accent}
      moduleBg={mc.bg}
    />
  );

  if (!catalogo || !informe) {
    return (
      <div className={styles.page}>
        {topbar}
        {error ? <div className={styles.error}>{error}</div> : <div className={styles.loading}>Cargando informes…</div>}
      </div>
    );
  }

  const grupos = catalogo.informes.reduce<{ titulo: string; items: typeof catalogo.informes }[]>((acc, i) => {
    const g = acc.find((x) => x.titulo === i.grupo);
    if (g) g.items.push(i);
    else acc.push({ titulo: i.grupo, items: [i] });
    return acc;
  }, []);
  const admite = (f: string) => informe.filtros.includes(f);

  return (
    <div className={styles.page}>
      {topbar}
      <div className={styles.layout}>
        <nav className={styles.aside} aria-label="Informes">
          {grupos.map((g) => (
            <div key={g.titulo} className={styles.group}>
              <div className={styles.groupLabel}>{g.titulo}</div>
              {g.items.map((i) => (
                <button key={i.id} type="button" aria-current={i.id === sel ? 'true' : undefined}
                  className={i.id === sel ? styles.itemOn : styles.item}
                  onClick={() => { setSel(i.id); setRes(null); }}>
                  <span className={styles.itemTitle}>{i.titulo}</span>
                  <span className={styles.itemDesc}>{i.descripcion}</span>
                </button>
              ))}
            </div>
          ))}
        </nav>

        <div className={styles.main}>
          <div className={styles.headRow}>
            <div>
              <h2 className={styles.title}>{res?.titulo ?? informe.titulo}</h2>
              <div className={styles.sub}>
                {informe.descripcion}{filtros ? ` · ${rotuloRango(filtros.desde, filtros.hasta)}` : ''}
              </div>
            </div>
            <div className={styles.actions}>
              <button type="button" className={styles.btnGhost} onClick={() => descargar('xlsx')} disabled={!res || !!descargando}>
                <DownloadSimple size={16} /> Excel
              </button>
              <button type="button" className={styles.btnGhost} onClick={() => descargar('csv', 'resumen')} disabled={!res || !!descargando}>
                CSV resumen
              </button>
              <button type="button" className={styles.btnGhost} onClick={() => descargar('csv', 'detalle')} disabled={!res || !!descargando}>
                CSV detalle
              </button>
              <button type="button" className={styles.btnPrimary} onClick={() => descargar('pdf')} disabled={!res || !!descargando}>
                <FilePdf size={16} /> {descargando === 'pdf' ? 'Generando…' : 'Descargar PDF'}
              </button>
            </div>
          </div>

          <div className={styles.controls}>
            <div className={styles.segment} role="radiogroup" aria-label="Periodo">
              {PERIODOS.map(([v, label]) => (
                <button key={v} type="button" role="radio" aria-checked={periodo === v}
                  className={periodo === v ? styles.segOn : styles.seg} onClick={() => setPeriodo(v)}>
                  {label}
                </button>
              ))}
            </div>
            {/* Las flechas actualizan a partir del valor VIGENTE (`setMes(m => …)`):
                con `moverMes(mes, …)` dos pulsaciones seguidas partían del mismo
                mes y la segunda se perdía. Lo destapó la captura automática. */}
            {periodo === 'mes' && mes && (
              <div className={styles.month}>
                <button type="button" className={styles.iconBtn} onClick={() => setMes((m) => m && moverMes(m, -1))} aria-label="Mes anterior">
                  <CaretLeft size={14} weight="bold" />
                </button>
                <span className={styles.monthLabel}>{rotuloMes(mes)}</span>
                <button type="button" className={styles.iconBtn} onClick={() => setMes((m) => m && moverMes(m, 1))} aria-label="Mes siguiente">
                  <CaretRight size={14} weight="bold" />
                </button>
              </div>
            )}
            {periodo === 'rango' && (
              <div className={styles.range}>
                <input type="date" value={rango.desde} onChange={(e) => setRango((r) => ({ ...r, desde: e.target.value }))} aria-label="Desde" />
                <span>–</span>
                <input type="date" value={rango.hasta} onChange={(e) => setRango((r) => ({ ...r, hasta: e.target.value }))} aria-label="Hasta" />
              </div>
            )}
            {admite('cliente') && (
              <select value={cliente} onChange={(e) => setCliente(e.target.value)} aria-label="Cliente">
                <option value="">Todos los clientes</option>
                {catalogo.clientes.map((c) => (
                  <option key={c.id} value={c.id}>{c.nombre}{c.activo ? '' : ' (desactivado)'}</option>
                ))}
                {!paraCliente && <option value="sin">Sin cliente</option>}
              </select>
            )}
            {admite('equipo') && !paraCliente && (
              <select value={equipo} onChange={(e) => setEquipo(e.target.value)} aria-label="Equipo">
                <option value="">Todos los equipos</option>
                {catalogo.equipos.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
              </select>
            )}
            {admite('canal') && (
              <select value={canal} onChange={(e) => setCanal(e.target.value)} aria-label="Canal">
                <option value="">Todos los canales</option>
                {catalogo.canales.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
            )}
            {admite('tecnico') && !paraCliente && (
              <select value={tecnico} onChange={(e) => setTecnico(e.target.value)} aria-label="Técnico">
                <option value="">Todos los técnicos</option>
                {catalogo.tecnicos.map((t) => (
                  <option key={t.id} value={t.id}>{t.nombre}{t.activo ? '' : ' (desactivado)'}</option>
                ))}
              </select>
            )}
          </div>

          <div className={styles.controls2}>
            <button type="button" role="switch" aria-checked={comparar} className={styles.switchRow}
              onClick={() => setComparar((v) => !v)}>
              <span className={comparar ? styles.switchOn : styles.switch}><span /></span>
              Comparar con periodo anterior
            </button>
            {informe.versionCliente && (
              <div className={styles.segmentSm} role="radiogroup" aria-label="Versión del informe">
                <button type="button" role="radio" aria-checked={!paraCliente}
                  className={!paraCliente ? styles.segOn : styles.seg} onClick={() => setVersion('interna')}>
                  Interna
                </button>
                <button type="button" role="radio" aria-checked={paraCliente}
                  className={paraCliente ? styles.segOn : styles.seg}
                  disabled={!cliente || cliente === 'sin'}
                  title={!cliente || cliente === 'sin' ? 'Elige antes un cliente' : undefined}
                  onClick={() => setVersion('cliente')}>
                  Para el cliente
                </button>
              </div>
            )}
          </div>

          {paraCliente && (
            <div className={styles.notice}>
              Versión para enviar al cliente: sin técnicos ni equipos, con las horas facturables y el desglose por categoría.
            </div>
          )}
          {error && <div className={styles.error}>{error}</div>}
          {res?.cambiosDesdeEmision && (
            <div className={styles.changed} role="status">
              <strong>Las horas de este periodo han cambiado desde que se sacó el informe</strong>
              {' '}({formatear(res.cambiosDesdeEmision.emitidoEl, 'fechahora', res.zona)}
              {res.cambiosDesdeEmision.emitidoPor ? `, ${res.cambiosDesdeEmision.emitidoPor}` : ''}):{' '}
              {res.cambiosDesdeEmision.apuntes === 1 ? '1 apunte' : `${res.cambiosDesdeEmision.apuntes} apuntes`},{' '}
              {conSigno(res.cambiosDesdeEmision.deltaMinutos)} en total y{' '}
              {conSigno(res.cambiosDesdeEmision.deltaFacturables)} facturables. Si ya se facturó, la cifra de abajo
              no es la que se mandó.
            </div>
          )}
          {cargando && !res && <div className={styles.loading}>Calculando…</div>}

          {res && (
            <div className={`${styles.report} ${cargando ? styles.stale : ''}`}>
              <div className={styles.kpis}>
                {res.kpis.map((k) => {
                  const d = res.comparar ? delta(k) : null;
                  return (
                    <div key={k.clave} className={styles.kpi}>
                      <span className={styles.kpiLabel}>{k.rotulo}</span>
                      <span className={styles.kpiValue}>{formatear(k.valor, k.tipo, res.zona)}</span>
                      {d ? (
                        <span className={styles.delta} data-tono={d.tono}>{d.texto}</span>
                      ) : k.nota ? (
                        <span className={styles.kpiNote}>{k.nota}</span>
                      ) : null}
                    </div>
                  );
                })}
              </div>

              <Grafica res={res} />

              <section className={styles.card}>
                <h3 className={styles.cardTitle}>Por {res.resumen.columnas[0]?.rotulo.toLowerCase()}</h3>
                <Tabla tabla={res.resumen} zona={res.zona} />
              </section>

              <section className={styles.card}>
                <h3 className={styles.cardTitle}>
                  Detalle <span className={styles.count}>{res.detalle.filas.length}</span>
                </h3>
                <Tabla tabla={res.detalle} zona={res.zona} />
              </section>

              <p className={styles.legend}>{res.leyenda}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
